/**
 * Skeleton loader components for each dashboard tab.
 * Uses Tailwind animate-pulse with bg-muted rounded rectangles
 * matching the approximate layout of each tab.
 */

// ============================================================================
// HELPER
// ============================================================================

function Bone({ className }: { className?: string }) {
  return <div className={`animate-pulse bg-muted rounded ${className ?? ''}`} />;
}

// ============================================================================
// VERDICTS TAB SKELETON
// ============================================================================

export function VerdictsSkeleton() {
  return (
    <div className="space-y-6">
      {/* Verdict cards row */}
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="p-4 rounded-xl border border-border-subtle space-y-3">
            <Bone className="h-4 w-16" />
            <Bone className="h-8 w-24" />
            <Bone className="h-3 w-20" />
          </div>
        ))}
      </div>
      {/* Trend chart area */}
      <Bone className="h-48 w-full rounded-xl" />
      {/* KPI summary row */}
      <div className="grid grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Bone key={i} className="h-20 w-full rounded-xl" />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// GATES TAB SKELETON
// ============================================================================

export function GatesSkeleton() {
  return (
    <div className="space-y-6">
      {/* Gate stats rows */}
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-border-subtle">
          <Bone className="h-5 w-32" />
          <Bone className="h-4 w-20" />
          <Bone className="h-4 flex-1" />
          <Bone className="h-4 w-16" />
        </div>
      ))}
      {/* Heatmap area */}
      <Bone className="h-64 w-full rounded-xl" />
    </div>
  );
}

// ============================================================================
// INSIGHTS TAB SKELETON
// ============================================================================

export function InsightsSkeleton() {
  return (
    <div className="space-y-6">
      {/* Sub-view tabs */}
      <div className="flex gap-2">
        {[0, 1, 2, 3].map((i) => (
          <Bone key={i} className="h-8 w-24 rounded-full" />
        ))}
      </div>
      {/* Main content area */}
      <Bone className="h-72 w-full rounded-xl" />
      {/* Supporting cards */}
      <div className="grid grid-cols-2 gap-4">
        <Bone className="h-32 w-full rounded-xl" />
        <Bone className="h-32 w-full rounded-xl" />
      </div>
    </div>
  );
}

// ============================================================================
// CREATIVES TAB SKELETON
// ============================================================================

export function CreativesSkeleton() {
  return (
    <div className="space-y-4">
      {/* Search/filter bar */}
      <div className="flex gap-3">
        <Bone className="h-10 w-64 rounded-lg" />
        <Bone className="h-10 w-32 rounded-lg" />
        <Bone className="h-10 w-32 rounded-lg" />
      </div>
      {/* Table header */}
      <div className="flex gap-4 px-4 py-3 border-b border-border-subtle">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Bone key={i} className="h-4 w-20" />
        ))}
      </div>
      {/* Table rows */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div key={i} className="flex gap-4 px-4 py-3">
          {[0, 1, 2, 3, 4, 5].map((j) => (
            <Bone key={j} className="h-4 w-20" />
          ))}
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// REPORTS TAB SKELETON
// ============================================================================

export function SkeletonCard({ lines = 4 }: { lines?: number }) {
  return (
    <div className="rounded-[14px] bg-surface shadow-sm p-4 space-y-3 animate-pulse">
      <Bone className="h-5 w-40" />
      <Bone className="h-3 w-24" />
      {Array.from({ length: lines }, (_, i) => (
        <Bone key={i} className="h-4 w-full" />
      ))}
    </div>
  );
}

// ============================================================================
// REPORTS TAB SKELETON
// ============================================================================

export function ReportsSkeleton() {
  return (
    <div className="space-y-6">
      {/* Report config panel */}
      <div className="p-4 rounded-xl border border-border-subtle space-y-3">
        <Bone className="h-4 w-32" />
        <div className="flex gap-3">
          <Bone className="h-10 w-48 rounded-lg" />
          <Bone className="h-10 w-48 rounded-lg" />
          <Bone className="h-10 w-48 rounded-lg" />
        </div>
      </div>
      {/* Report preview table */}
      <Bone className="h-64 w-full rounded-xl" />
      {/* Saved reports list */}
      {[0, 1, 2].map((i) => (
        <Bone key={i} className="h-16 w-full rounded-xl" />
      ))}
    </div>
  );
}
